/// <reference types="vite/client" />

// GET /api/v2/command-center/:clientId/:useCaseId

import { dedupeGet, eaCaches, readTtl, writeTtl } from '../utils/sharedTtlCache';

const API_BASE =
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_BACKEND_URL ||
  'http://localhost:8080';

export interface CommandCenterSummaryMetric {
  id: string;
  label: string;
  value: string | CommandCenterOverviewPriority | null;
  value_display: string | CommandCenterOverviewPriority | null;
  description?: string | null;
  progress_percent?: number;
  complete?: number;
  total?: number;
  open_high_severity?: number;
  status?: string;
  milestone_id?: string;
}

export interface CommandCenterHeader {
  use_case_id: number;
  name: string;
  title: string;
  breadcrumb: string;
  category_name: string;
  initiative_phase: string | null;
  status: string | null;
  status_display: string | null;
  initiative_owner: string | null;
  executive_sponsor: string | null;
  start_date: string | null;
  target_end_date: string | null;
  target_timeline_display: string | null;
  payback_months: number | null;
  payback_display: string | null;
  base_roi_range_display: string | null;
  confidence_score_pct: number | null;
  confidence_level: string | null;
  initiative_duration_months: number | null;
  initiative_duration_display: string | null;
  summary_metrics: CommandCenterSummaryMetric[];
}

export interface CommandCenterChecklistItem {
  index: number;
  label: string;
  done: boolean;
  status: string;
}

export interface CommandCenterWorkstream {
  workstream_id: string;
  name: string;
  description: string | null;
  owner_role: string | null;
  owner: string | null;
  owner_display: string | null;
  months_from_start: number | null;
  due_date: string | null;
  target: string | null;
  target_date: string | null;
  status: string;
  notes: string | null;
  pipeline_notes: string | null;
  /** True while the row is a template placeholder that has not been persisted yet. */
  is_default?: boolean;
}

export interface CommandCenterMilestone {
  index: number;
  milestone_id: string;
  label: string;
  title: string;
  phase: string;
  owner_role: string | null;
  owner: string | null;
  owner_display: string | null;
  months_from_start: number | null;
  due_date: string | null;
  target_date: string | null;
  target: string | null;
  status: string;
  completion_pct: number;
  progress_percent: number;
  blocker: string | null;
  value_note: string | null;
  depends_on: string | null;
  dependency_warning: string | null;
}

export interface CommandCenterRisk {
  index: number;
  risk_id: string;
  name: string;
  title: string;
  description: string;
  category: string;
  severity: string;
  severity_display: string;
  likelihood: string;
  mitigation: string;
  mitigation_plan: string;
  owner: string;
  status: string;
}

export interface CommandCenterTeamMember {
  role?: string;
  name?: string;
  title?: string;
  owner_role?: string;
  owner_display?: string;
  team_member_id?: string;
  index?: number;
  email?: string;
}

export interface CommandCenterApprovalStep {
  id: string;
  label: string;
  cfo?: boolean;
  status: 'done' | 'current' | 'pending' | string;
  source?: string | null;
}

export interface CommandCenterApprovalSignoff {
  completed_count: number;
  current_step_id: string | null;
  steps: CommandCenterApprovalStep[];
  all_complete?: boolean;
}

export interface CommandCenterKpi {
  index: number;
  kpi_id: string;
  category: string;
  label: string;
  baseline_value: string | null;
  target_value: string | null;
  current_value: string | null;
  current_value_display: string | null;
  unit: string | null;
  editable: boolean;
}

export interface CommandCenterDeliveryMetric {
  index: number;
  label: string;
  category: string;
  baseline_value: string | null;
  current_value: string | null;
  current_value_pct?: string | null;
  current_value_display?: string | null;
  target_value: string | null;
  target_value_pct?: string | null;
  target_value_detail?: string | null;
  unit: string | null;
  status: string | null;
  read_only: boolean;
}

export interface CommandCenterResourceItem {
  id: string;
  title: string;
  description: string;
  action: 'render' | 'navigate' | 'download' | 'resource' | 'tab';
  format?: string;
  available: boolean;
  path?: string;
  alias_path?: string;
  filename?: string;
  resource_id?: string;
  target?: string;
  content?: Record<string, unknown>;
}

export interface CommandCenterOverviewRisk {
  risk_id: string;
  name: string;
  title: string;
  description: string;
  severity: string;
  severity_display: string;
  status: string;
}

export interface CommandCenterOverviewPriority {
  label: string;
  phase?: string;
  due_date?: string | null;
  due_date_display?: string | null;
  months_from_start?: number;
  status?: string;
}

export interface CommandCenterOverviewTab {
  uc_name?: string;
  uc_description?: string;
  domain?: string;
  category?: string;
  initiative_status?: string;
  initiative_status_display?: string;
  initiative_phase?: string;
  initiative_objective?: string;
  primary_value_driver?: string;
  execution_rationale?: string;
  why_initiative_approved?: string;
  expected_outcomes?: Array<string | CommandCenterOverviewPriority>;
  recommended_next_action?: string;
  top_priorities?: Array<string | CommandCenterOverviewPriority>;
  top_risks?: CommandCenterOverviewRisk[];
  immediate_next_actions?: string[];
  profile_sections?: Record<string, string | null>;
}

export interface CommandCenterQuickAction {
  id: string;
  label: string;
  action: string;
  target?: string;
  path?: string;
  resource_id?: string;
}

export interface CommandCenterResponse {
  success: boolean;
  client_id: string;
  use_case_id: number;
  generated_at?: string;
  header: CommandCenterHeader;
  approval_signoff?: CommandCenterApprovalSignoff | null;
  initiative_metadata?: {
    initiative_owner: string | null;
    executive_sponsor: string | null;
    initiative_phase: string | null;
    start_date: string | null;
    target_end_date: string | null;
    target_end_date_is_computed?: boolean;
    value_realization: string | null;
    value_realization_display: string | null;
  };
  sidebar: {
    initiative_status: {
      status: string | null;
      status_display: string | null;
    };
    initiative_metadata?: {
      initiative_owner: string | null;
      executive_sponsor: string | null;
      initiative_phase: string | null;
      start_date: string | null;
      target_end_date: string | null;
      target_end_date_is_computed?: boolean;
      value_realization: string | null;
      value_realization_display: string | null;
    };
    next_milestone: {
      milestone_id: string;
      label: string;
      title: string;
      due_date: string | null;
      due_date_display: string | null;
      completion_pct: number;
      progress_display: string;
      status: string;
    } | null;
    top_risk: CommandCenterRisk | null;
    quick_actions: CommandCenterQuickAction[];
  };
  tabs: {
    overview: CommandCenterOverviewTab | null;
    plans: {
      activation_checklist_summary: {
        total: number;
        completed: number;
        pending: number;
        progress_percent: number;
      };
      activation_checklist: CommandCenterChecklistItem[];
      workstreams: CommandCenterWorkstream[];
    } | null;
    milestones: {
      milestone_summary: {
        total: number;
        complete: number;
        in_progress: number;
        not_started: number;
        delayed: number;
      };
      milestones: CommandCenterMilestone[];
      export_path?: string;
    } | null;
    risks: {
      risk_summary: {
        total: number;
        high_severity: number;
        moderate: number;
        low: number;
        critical: number;
        open: number;
        mitigated: number;
        resolved: number;
      };
      top_risk: CommandCenterRisk | null;
      risks: CommandCenterRisk[];
    } | null;
    team: {
      team_summary: { total: number };
      team: CommandCenterTeamMember[];
    } | null;
    metrics: {
      value_realization_banner: string | null;
      financial_kpis: Record<string, unknown> | null;
      kpis: CommandCenterKpi[];
      delivery_metrics: CommandCenterDeliveryMetric[];
    } | null;
    resources: {
      approval_materials: CommandCenterResourceItem[];
      decision_references: CommandCenterResourceItem[];
      execution_materials: CommandCenterResourceItem[];
      profile_sections?: Record<string, string | null>;
      paths?: Record<string, string>;
    } | null;
  };
}

export class CommandCenterError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'CommandCenterError';
    this.status = status;
  }
}

const COMMAND_CENTER_TTL_MS = 60_000;

function commandCenterUrl(clientId: string, useCaseId: string | number): string {
  return `${API_BASE}/api/v2/command-center/${encodeURIComponent(clientId)}/${encodeURIComponent(String(useCaseId))}`;
}

export function peekCommandCenter(
  clientId: string,
  useCaseId: string | number
): CommandCenterResponse | null {
  return readTtl(eaCaches().commandCenter, commandCenterUrl(clientId, useCaseId), COMMAND_CENTER_TTL_MS);
}

export async function fetchCommandCenter(
  clientId: string,
  useCaseId: string | number
): Promise<CommandCenterResponse> {
  if (!clientId || useCaseId == null || useCaseId === '') {
    throw new CommandCenterError('Client ID and use case ID are required.');
  }

  const url = commandCenterUrl(clientId, useCaseId);
  const cached = peekCommandCenter(clientId, useCaseId);
  if (cached) return cached;

  return dedupeGet(url, async () => {
    let res: Response;
    try {
      res = await fetch(url, { credentials: 'include' });
    } catch {
      throw new CommandCenterError(
        'Unable to reach the server. Check that the backend is running on port 8080.'
      );
    }

    if (!res.ok) {
      let message = `Failed to load command center (${res.status})`;
      try {
        const body = await res.json();
        if (body?.message) message = body.message;
        else if (body?.error) message = body.error;
      } catch { /* ignore */ }
      throw new CommandCenterError(message, res.status);
    }

    const data = (await res.json()) as CommandCenterResponse;
    if (!data.success) {
      throw new CommandCenterError('Command center response was not successful.');
    }
    writeTtl(eaCaches().commandCenter, url, data);
    return data;
  });
}

export function resolveApiPath(path: string): string {
  if (path.startsWith('http')) return path;
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Drops the cached GET so the next load reflects a write. */
export function invalidateCommandCenter(clientId: string, useCaseId: string | number): void {
  eaCaches().commandCenter.delete(commandCenterUrl(clientId, useCaseId));
}

export interface WorkstreamWritePayload {
  name?: string;
  owner?: string;
  status?: string;
  /** A date the backend can parse, or 'Ongoing' to clear the target. */
  target?: string;
  notes?: string;
}

async function writeWorkstream<T>(
  method: 'POST' | 'PATCH' | 'DELETE',
  clientId: string,
  useCaseId: string | number,
  path: string,
  payload?: WorkstreamWritePayload,
): Promise<T> {
  if (!clientId || useCaseId == null || useCaseId === '') {
    throw new CommandCenterError('Client ID and use case ID are required.');
  }

  const url = `${commandCenterUrl(clientId, useCaseId)}${path}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'include',
      headers: payload ? { 'Content-Type': 'application/json' } : undefined,
      body: payload ? JSON.stringify(payload) : undefined,
    });
  } catch {
    throw new CommandCenterError(
      'Unable to reach the server. Check that the backend is running on port 8080.'
    );
  }

  let body: { success?: boolean; error?: string; message?: string } & Record<string, unknown> = {};
  try {
    body = await res.json();
  } catch { /* empty or non-JSON body */ }

  if (!res.ok || body?.success === false) {
    throw new CommandCenterError(
      body?.message || body?.error || `Request failed (${res.status})`,
      res.status,
    );
  }

  invalidateCommandCenter(clientId, useCaseId);
  return body as T;
}

export async function createWorkstream(
  clientId: string,
  useCaseId: string | number,
  payload: WorkstreamWritePayload,
): Promise<CommandCenterWorkstream | null> {
  const body = await writeWorkstream<{ workstream?: CommandCenterWorkstream }>(
    'POST', clientId, useCaseId, '/workstreams', payload,
  );
  return body.workstream ?? null;
}

export async function updateWorkstream(
  clientId: string,
  useCaseId: string | number,
  workstreamId: string,
  payload: WorkstreamWritePayload,
): Promise<CommandCenterWorkstream | null> {
  const body = await writeWorkstream<{ workstream?: CommandCenterWorkstream }>(
    'PATCH', clientId, useCaseId, `/workstreams/${encodeURIComponent(workstreamId)}`, payload,
  );
  return body.workstream ?? null;
}

export async function deleteWorkstream(
  clientId: string,
  useCaseId: string | number,
  workstreamId: string,
): Promise<void> {
  await writeWorkstream(
    'DELETE', clientId, useCaseId, `/workstreams/${encodeURIComponent(workstreamId)}`,
  );
}

export interface MilestoneWritePayload {
  status?: 'Not Started' | 'In Progress' | 'Complete' | 'Delayed' | string;
  completion_pct?: number;
  owner?: string;
  blocker?: string;
}

/**
 * PATCH /api/v2/command-center/:clientId/:useCaseId/milestones/:milestoneId
 * Persists Path to Value / Command Center milestone status into uc_milestone_tracking.
 */
export async function updateMilestone(
  clientId: string,
  useCaseId: string | number,
  milestoneId: string,
  payload: MilestoneWritePayload,
): Promise<CommandCenterMilestone | null> {
  if (!clientId || useCaseId == null || useCaseId === '' || !milestoneId) {
    throw new CommandCenterError('Client ID, use case ID, and milestone ID are required.');
  }

  const url = `${commandCenterUrl(clientId, useCaseId)}/milestones/${encodeURIComponent(milestoneId)}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new CommandCenterError(
      'Unable to reach the server. Check that the backend is running on port 8080.',
    );
  }

  let body: {
    success?: boolean;
    error?: string;
    message?: string;
    milestone?: CommandCenterMilestone;
  } = {};
  try {
    body = await res.json();
  } catch { /* empty or non-JSON body */ }

  if (!res.ok || body?.success === false) {
    throw new CommandCenterError(
      body?.message || body?.error || `Request failed (${res.status})`,
      res.status,
    );
  }

  invalidateCommandCenter(clientId, useCaseId);
  return body.milestone ?? null;
}

export interface ActivationChecklistWritePayload {
  index: number;
  done: boolean;
}

/**
 * PATCH /api/v2/command-center/:clientId/:useCaseId/activation-checklist
 * Persists an explicit check/uncheck override for a readiness checklist item.
 */
export async function updateActivationChecklistItem(
  clientId: string,
  useCaseId: string | number,
  payload: ActivationChecklistWritePayload,
): Promise<{
  activation_checklist: CommandCenterChecklistItem[];
  activation_checklist_summary: {
    total: number;
    completed: number;
    pending: number;
    progress_percent: number;
  } | null;
}> {
  if (!clientId || useCaseId == null || useCaseId === '') {
    throw new CommandCenterError('Client ID and use case ID are required.');
  }

  const url = `${commandCenterUrl(clientId, useCaseId)}/activation-checklist`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new CommandCenterError(
      'Unable to reach the server. Check that the backend is running on port 8080.',
    );
  }

  let body: {
    success?: boolean;
    error?: string;
    message?: string;
    activation_checklist?: CommandCenterChecklistItem[];
    activation_checklist_summary?: {
      total: number;
      completed: number;
      pending: number;
      progress_percent: number;
    } | null;
  } = {};
  try {
    body = await res.json();
  } catch { /* empty or non-JSON body */ }

  if (!res.ok || body?.success === false) {
    throw new CommandCenterError(
      body?.message || body?.error || `Request failed (${res.status})`,
      res.status,
    );
  }

  invalidateCommandCenter(clientId, useCaseId);
  return {
    activation_checklist: body.activation_checklist ?? [],
    activation_checklist_summary: body.activation_checklist_summary ?? null,
  };
}

/**
 * POST /api/v2/command-center/:clientId/:useCaseId/approval-signoff/complete
 * Completes the current Approval & Sign-off stage (sequential).
 */
export async function completeApprovalSignoff(
  clientId: string,
  useCaseId: string | number,
): Promise<CommandCenterApprovalSignoff | null> {
  if (!clientId || useCaseId == null || useCaseId === '') {
    throw new CommandCenterError('Client ID and use case ID are required.');
  }

  const url = `${commandCenterUrl(clientId, useCaseId)}/approval-signoff/complete`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
  } catch {
    throw new CommandCenterError(
      'Unable to reach the server. Check that the backend is running on port 8080.',
    );
  }

  let body: {
    success?: boolean;
    error?: string;
    message?: string;
    approval_signoff?: CommandCenterApprovalSignoff;
  } = {};
  try {
    body = await res.json();
  } catch { /* empty */ }

  if (!res.ok || body?.success === false) {
    throw new CommandCenterError(
      body?.message || body?.error || `Request failed (${res.status})`,
      res.status,
    );
  }

  invalidateCommandCenter(clientId, useCaseId);
  return body.approval_signoff ?? null;
}

export interface TeamMemberWritePayload {
  name: string;
  role: string;
  title?: string;
  email?: string;
}

/**
 * POST /api/v2/command-center/:clientId/:useCaseId/team
 */
export async function createTeamMember(
  clientId: string,
  useCaseId: string | number,
  payload: TeamMemberWritePayload,
): Promise<{
  team_member_id?: string;
  name?: string;
  role?: string;
  title?: string;
  approval_signoff?: CommandCenterApprovalSignoff | null;
}> {
  if (!clientId || useCaseId == null || useCaseId === '') {
    throw new CommandCenterError('Client ID and use case ID are required.');
  }

  const url = `${commandCenterUrl(clientId, useCaseId)}/team`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new CommandCenterError(
      'Unable to reach the server. Check that the backend is running on port 8080.',
    );
  }

  let body: {
    success?: boolean;
    error?: string;
    message?: string;
    team_member_id?: string;
    name?: string;
    role?: string;
    title?: string;
    approval_signoff?: CommandCenterApprovalSignoff;
  } = {};
  try {
    body = await res.json();
  } catch { /* empty */ }

  if (!res.ok || body?.success === false) {
    throw new CommandCenterError(
      body?.message || body?.error || `Request failed (${res.status})`,
      res.status,
    );
  }

  invalidateCommandCenter(clientId, useCaseId);
  return body;
}

/**
 * DELETE /api/v2/command-center/:clientId/:useCaseId/team/:teamMemberId
 */
export async function deleteTeamMember(
  clientId: string,
  useCaseId: string | number,
  teamMemberId: string,
): Promise<void> {
  if (!clientId || useCaseId == null || useCaseId === '' || !teamMemberId) {
    throw new CommandCenterError('Client ID, use case ID, and team member ID are required.');
  }

  const url = `${commandCenterUrl(clientId, useCaseId)}/team/${encodeURIComponent(teamMemberId)}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch {
    throw new CommandCenterError(
      'Unable to reach the server. Check that the backend is running on port 8080.',
    );
  }

  let body: { success?: boolean; error?: string; message?: string } = {};
  try {
    body = await res.json();
  } catch { /* empty */ }

  if (!res.ok || body?.success === false) {
    throw new CommandCenterError(
      body?.message || body?.error || `Request failed (${res.status})`,
      res.status,
    );
  }

  invalidateCommandCenter(clientId, useCaseId);
}
